import { create } from 'zustand';
import { Editor, Box, TLAssetId, TLShapeId } from '@tldraw/editor';
import { combine } from 'zustand/middleware';
import { DocProps } from '@/types/api';

interface PdfPage {
  src: string;
  bounds: Box;
  assetId: TLAssetId;
  shapeId: TLShapeId;
}

interface Pdf {
  name: string;
  pages: PdfPage[];
  source: string;
}

interface BoardPdfState {
  pdfGlobal: Pdf | undefined;
  activeDoc: DocProps | null;
  pdfEditor: Editor | null;
  exportPdf: ((editor: Editor, data: Pdf) => Promise<void>) | null;
  uploadDocs: DocProps[];
  studentDocs: DocProps[];
}

interface BoardPdfActions {
  setPdfGlobal: (pdfGlobal: Pdf | undefined) => void;
  setActiveDoc: (activeDoc: DocProps | null) => void;
  setPdfEditor: (pdfEditor: Editor | null) => void;
  setExportPdf: (fn: BoardPdfState['exportPdf']) => void;
  setUploadDocs: (docs: DocProps[]) => void;
  addUploadDoc: (newDoc: DocProps) => void;
  deleteUploadDoc: (id: string) => void;
  setStudentDocs: (docs: DocProps[]) => void;
  addStudentDoc: (doc: DocProps) => void;
  removeStudentDoc: (id: string) => void;
  resetFiles: () => void;
}

export const useBoardPdfStore = create(
  combine<BoardPdfState, BoardPdfActions>(
    {
      pdfGlobal: undefined,
      activeDoc: null,
      pdfEditor: null,
      exportPdf: null,
      uploadDocs: [],
      studentDocs: []
    },
    (set) => ({
      setPdfGlobal: (pdfGlobal) => set({ pdfGlobal }),
      setActiveDoc: (activeDoc) => set({ activeDoc }),
      setPdfEditor: (pdfEditor) => set({ pdfEditor }),
      setExportPdf: (fn) => set({ exportPdf: fn }),
      setUploadDocs: (docs) => set({ uploadDocs: docs }),
      addUploadDoc: (newDoc) =>
        set((state) => ({
          uploadDocs: state.uploadDocs.some((item) => item.id === newDoc.id)
            ? state.uploadDocs
            : [...state.uploadDocs, newDoc]
        })),
      deleteUploadDoc: (id) =>
        set((state) => ({
          uploadDocs: state.uploadDocs.filter((item) => item.id !== id)
        })),
      setStudentDocs: (docs) => set({ studentDocs: docs }),
      addStudentDoc: (doc) =>
        set((state) => ({
          studentDocs: state.studentDocs.some((item) => item.id === doc.id)
            ? state.studentDocs
            : [...state.studentDocs, doc]
        })),
      removeStudentDoc: (id) =>
        set((state) => ({
          studentDocs: state.studentDocs.filter((item) => item.id !== id)
        })),
      resetFiles: () =>
        set({
          pdfGlobal: undefined,
          activeDoc: null,
          pdfEditor: null,
          exportPdf: null,
          uploadDocs: [],
          studentDocs: []
        })
    })
  )
);
